import { Component, OnInit, Output, EventEmitter, computed, Signal, effect, OnDestroy } from '@angular/core';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { SearchEntity, SearchHasResult } from '@airwallet/shared-models/search';
import { DashboardOwnerUser } from '@dashboard_models/dashboard-owner-user';
import { HelperService } from '@services/helper/helper.service';
import { Router } from '@angular/router';
import { FESorted, SearchService } from '@services/search/search-service';
import { MixpanelService } from '@services/mixpanel/mixpanel.service';
import { AccountState } from '@airwallet/shared-models/details';
import { SearchSuggestions, searchSuggestions } from './search-suggestions';
import { AuthService } from '@services/auth/auth.service';
import { FeatureService } from '@services/feature-toggle/feature.service';
import { FeatureAccessKeys } from '@airwallet/shared-models/feature_access';
import { SidebarService } from '@services/sidebar/sidebar.service';

@Component({
    selector: 'app-search-preview',
    templateUrl: './search-preview.component.html',
    styleUrls: ['./search-preview.component.scss'],
    imports: [TranslateModule]
})
export class SearchPreviewComponent implements OnInit, OnDestroy {
    @Output() itemClickedEvent: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() searchSuggestionSelected: EventEmitter<string> = new EventEmitter<string>();

    private keydownHandler = (e: KeyboardEvent) => this.handleKey(e);

    searchResultAmount: Signal<number> = computed(() => {
        let count = 0;
        for (const category in this.searchService.fuzzySearchResult()) {
            const value = this.searchService.fuzzySearchResult()[category];
            if (Array.isArray(value) && category !== 'transactions') {
                count = count + this.searchService.fuzzySearchResult()[category].length;
            } else if (category === 'totalTransactions') {
                count = count + value;
            }
        }
        return count;
    });

    filteredSuggestions: Signal<SearchSuggestions> = computed(() => {
        const input: string = this.searchService.searchInputValue().toLowerCase();
        if (!input) return this.searchSuggestions;

        return this.searchSuggestions.filter(suggestion => {
            const code: string = suggestion.short_code?.toLowerCase() || '';
            const label: string = this.translate.instant(suggestion.translation_code)?.toLowerCase() || '';
            const codeMatch: boolean = code && (input.includes(code) || code.includes(input));
            const labelMatch: boolean = label && (input.includes(label) || label.includes(input));
            return codeMatch || labelMatch;
        });
    });

    searchInputValue: Signal<string> = this.searchService.searchInputValue;
    searchFuzzyCategories: Signal<string[]> = computed(() => {
        return Object.keys(this.searchService.fuzzySearchResult()).filter(key => this.searchService.fuzzySearchResult()[key].length > 0) as (keyof FESorted)[];
    });
    searchResultCategories: Signal<string[]> = computed(() => Object.keys(this.searchService.fullSearchResult()).filter(key => this.searchService.fullSearchResult()[key].length > 0) as (keyof SearchEntity)[]);

    currentChosenId: string;
    user: DashboardOwnerUser;
    resultMap: Signal<Record<string, number>> = computed(() => {
        const map: Record<string, number> = { all: 0 };
        let count = 1;
        for (const key in this.searchService.fullSearchResult()) {
            map[key] = count;
            count++;
        }
        return map;
    });
    fuzzyResultMap: Signal<Record<string, number>> = computed(() => {
        const map: Record<string, number> = { all: 0 };
        let count = 1;
        for (const key in this.searchService.fuzzySearchResult()) {
            map[key] = count;
            count++;
        }
        return map;
    });
    liList: NodeListOf<HTMLLIElement>;
    lastElement: HTMLLIElement;
    searchSuggestions: SearchSuggestions = searchSuggestions;

    contractFeatureAccessKeys: FeatureAccessKeys = {
        featureCategoryKey: 'search',
        featureActionKey: 'contract_id'
    };

    last4FeatureAccessKeys: FeatureAccessKeys = {
        featureCategoryKey: 'search',
        featureActionKey: 'last4'
    };

    constructor(
        protected authService: AuthService,
        public translate: TranslateService,
        public helperService: HelperService,
        private mixpanelService: MixpanelService,
        private featureService: FeatureService,
        private router: Router,
        protected searchService: SearchService,
        protected sidebarService: SidebarService
    ) {
        effect(() => {
            const inputValue = this.searchService.searchInputValue();
            setTimeout(() => {
                // This timeout is needed to let the DOM update before we try to get the liList
                console.log('inputValue', inputValue);
            }, 0);
            return inputValue;
        });
    }

    async ngOnInit() {
        this.accessFilterSearchSuggestions();
        this.user = this.helperService.getDashboardOwnerUser();
        if (!this.sidebarService.isBelowMobileWidth()) {
            document.addEventListener('keydown', this.keydownHandler);
        }
        setTimeout(() => {
            this.updateLiList();
            this.lastElement = this.liList[0];
            if (this.lastElement) {
                this.currentChosenId = this.lastElement.id;
            }
        }, 0);
    }

    ngOnDestroy() {
        if (!this.sidebarService.isBelowMobileWidth()) {
            document.removeEventListener('keydown', this.keydownHandler);
        }
    }

    updateLiList() {
        this.liList = document.querySelectorAll('.ul-list li');
    }

    accessFilterSearchSuggestions(): void {
        if (!this.featureService.getFeatureAccess(this.contractFeatureAccessKeys.featureCategoryKey, this.contractFeatureAccessKeys.featureActionKey)()) {
            this.searchSuggestions = this.searchSuggestions.filter(suggestion => suggestion.id !== this.contractFeatureAccessKeys.featureActionKey);
            this.contractFeatureAccessKeys.locked = true;
        }
        if (!this.featureService.getFeatureAccess('search', 'last4')()) {
            this.searchSuggestions = this.searchSuggestions.filter(suggestion => suggestion.id !== this.last4FeatureAccessKeys.featureActionKey);
            this.contractFeatureAccessKeys.locked = true;
        }
    }

    async navigate(item: SearchEntity) {
        this.mixpanelService.track(`Search Category: ${item.t}`, { category: item.t });
        this.mixpanelService.identify(); // Associate actions with the user

        interface RouteType {
            path: string;
            params?: object;
        }
        const route: RouteType = await this.searchService.getPath(item, this.user.uid);

        if (route.path.length <= 0 && Object.keys(route.params).length <= 0) {
            this.itemClickedEvent.emit(true);
            return;
        }

        if (this.sidebarService.isBelowMobileWidth()) {
            setTimeout(() => {
                this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
                    this.router.navigate([route.path], { queryParams: route.params });
                });
                this.currentChosenId = '';
                this.itemClickedEvent.emit(true);
            }, 200);
        } else {
            this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
                this.router.navigate([route.path], { queryParams: route.params });
            });

            this.itemClickedEvent.emit(true);
        }
        this.searchService.isSearchDropdownOpened.set(false);
    }

    async viewFullSearch(id: string) {
        this.mixpanelService.track(`Search Category: full view`, { search_value: this.searchService.searchQuery() });
        this.mixpanelService.identify(); // Associate actions with the user

        this.searchService.fullSearch(this.searchService.searchQuery()).then(() => {
            this.searchService.isSearchDropdownOpened.set(false);
        });

        if (this.sidebarService.isBelowMobileWidth()) {
            this.currentChosenId = id;
            this.router.navigate(['search'], {
                queryParams: {
                    q: this.searchService.searchQuery()
                }
            });
            this.itemClickedEvent.emit(true);
            this.currentChosenId = '';
        } else {
            this.router.navigate(['search'], {
                queryParams: {
                    q: this.searchService.searchQuery()
                }
            });
            this.itemClickedEvent.emit(true);
        }
    }

    async changeHoverStyle(event: MouseEvent) {
        if (event.type === 'mouseenter') {
            this.currentChosenId = (event.target as HTMLElement).id;
            this.lastElement = event.target as HTMLLIElement;
        } else if (event.type === 'mouseleave') {
            this.currentChosenId = '';
            this.lastElement = null;
        }
    }

    async handleKey(event: KeyboardEvent) {
        if (event.key?.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey)) {
            return;
        }
        this.updateLiList();
        if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown' && event.key !== 'Enter' && event.key !== 'Tab') {
            // This resets the blue bar to the "Show all results" everytime you type a new character
            if (event.key.length === 1 || event.key === 'Backspace' || event.key === 'Delete') {
                this.lastElement = this.liList[0];
                if (this.lastElement) {
                    this.currentChosenId = this.lastElement.id;
                }
            }
            return;
        }

        if (this.sidebarService.isBelowMobileWidth()) {
            return;
        }

        if (this.currentChosenId === '' || !this.lastElement) {
            event.preventDefault();
            this.currentChosenId = `${this.resultMap()['all']}-0`;
            this.lastElement = this.liList[0];
            return;
        }

        let index: number = this.lastElement ? [].indexOf.call(this.liList, this.lastElement) : 0;

        switch (event.key) {
            case 'ArrowUp':
                //Prevents the scroll bar to also be navigated
                event.preventDefault();

                // Set up a counter to keep track of which <li> is selected
                if (index > 0) {
                    index--; // Decrease the counter
                } else {
                    index = this.liList.length - 1; // Go to the last <li> when at the top
                }

                this.lastElement = this.liList[index];
                this.currentChosenId = this.lastElement.id;
                break;

            case 'ArrowDown':
                //Prevents the scroll bar to also be navigated
                event.preventDefault();

                if (index < this.liList.length - 1) {
                    index++; // Increase counter
                } else {
                    index = 0; // Go to the first <li> when at the bottom
                }
                this.lastElement = this.liList[index];
                this.currentChosenId = this.lastElement.id;
                break;
            case 'Enter':
                if (index === 0) {
                    this.searchService.isSearchDropdownOpened.set(false);
                }
                if (this.liList[index]) {
                    if (!this.searchSuggestions.some(item => item.id === this.currentChosenId)) {
                        // should only emit if it is a search result select and not a suggestion select
                        (this.liList[index] as HTMLElement).click();
                        this.itemClickedEvent.emit(true);
                    } else {
                        event.preventDefault();
                        const shortCode: string = this.searchSuggestions.find(item => item.id === this.currentChosenId)?.short_code || '';
                        this.searchSuggestionSelected.emit(shortCode);
                        this.lastElement = this.liList[0];
                        this.currentChosenId = this.lastElement.id;
                    }
                }
                break;
            case 'Tab':
                event.preventDefault();
                if (index < this.liList.length - 1) {
                    index++; // Increase counter
                } else {
                    index = 0; // Go to the first <li> when at the bottom
                }
                this.lastElement = this.liList[index];
                this.currentChosenId = this.lastElement.id;
                break;
        }
    }

    isCurrentId(id: string): boolean {
        if (id === this.currentChosenId) {
            return true;
        } else {
            return false;
        }
    }

    getStatusString(status: AccountState): string {
        return this.translate.instant(`customers.${status}`);
    }

    roundPerform(num: number | null): string {
        return num ? String(`${num < 0 ? '' : '+'}${this.helperService.roundToTwoDecimals(num * 100)}%`) : '';
    }

    getDate(timestamp: number): string {
        return this.helperService.getLocalizedDate(timestamp);
    }

    selectSuggestion(shortCode: string) {
        this.searchSuggestionSelected.emit(shortCode);
    }

    protected readonly SearchHasResult = SearchHasResult;
}
